import React, { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Loader2, Sparkles, Pencil } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { api, getApiErrorMessage } from "../../api/client";
import { unwrapArray } from "../../lib/api-envelope";
import { tenantTodayDate, toCalendarDateString, todayCalendarDate } from "../../lib/calendar-date";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { fmtCurrency, parseMoney } from "../../lib/display-labels";
import type { FieldTicket } from "../../pages/rentals/rental-field-ticket";

/**
 * New Field Ticket: pick an active order, the API prefills the lines and the
 * money, the office reviews and creates the ticket. Moved out of
 * RentalFieldTickets.tsx unchanged when the register and the ticket became two
 * pages (#815); it opens from the register's band.
 */

/**
 * `GET /surface-rentals/orders` and `/orders/:id` return snake_case:
 * `order_number`, `customer_name`, `well_name`, `lsd`, `afe_number`,
 * `billing_trigger`. Three of the six names this screen read do not exist on
 * the payload, and each one fed a fallback that looked like data — the order's
 * raw UUID where its number belongs, and the literal "Unknown" for the
 * customer and the well, on a ticket the customer is billed from.
 *
 * Reads tolerate both spellings per CLAUDE.md.
 */
interface OrderOption {
  id: string;
  order_number?: string;
  orderNumber?: string;
  customer?: string;
  customer_name?: string;
  customerName?: string;
  well?: string;
  well_name?: string;
  lsd?: string;
  afe_number?: string;
  billing_trigger?: string;
  status?: string;
}

const orderNumberOf = (o?: OrderOption | null) =>
  o?.order_number ?? o?.orderNumber ?? undefined;
const customerOf = (o?: OrderOption | null) =>
  o?.customer_name ?? o?.customerName ?? o?.customer ?? undefined;
const wellOf = (o?: OrderOption | null) => o?.well_name ?? o?.well ?? undefined;

interface OrderAsset {
  id: string;
  name: string;
  asset_id: string;
  category: string;
  /** Null when neither the order line nor the asset carries a rate. Not 0. */
  daily_rate: number | null;
  on_rent_since?: string;
  days_on_rent?: number;
}

/**
 * A row from `GET /surface-rentals/orders/:orderId/assets`:
 * `rental_order_assets.*` plus `asset_number`, `asset_name`, `category` and
 * `asset_status` from the join to `rental_assets`.
 *
 * `OrderAsset` above is what this screen wants; the two differ on three fields,
 * and the ones that differ are the ones that price the ticket.
 */
interface OrderAssetApiRow {
  id: string;
  asset_id: string;
  asset_name?: string | null;
  asset_number?: string | null;
  category?: string | null;
  daily_rate?: string | number | null;
  applied_rate?: string | number | null;
  delivered_at?: string | null;
  returned_at?: string | null;
}

/**
 * NUMERIC arrives from pg as a string, so `daily_rate` needs coercing rather
 * than trusting. `applied_rate` is the negotiated rate for THIS order and wins
 * over the asset's list rate when it is set.
 */
function normalizeOrderAsset(row: OrderAssetApiRow): OrderAsset {
  const rate = row.applied_rate ?? row.daily_rate;
  const n = rate === null || rate === undefined || rate === "" ? NaN : Number(rate);

  return {
    id: row.id,
    // The join names it `asset_name`. Read as `name` it was undefined, and the
    // AI prefill payload below sends it as the line-item description.
    name: row.asset_name ?? row.asset_number ?? "",
    asset_id: row.asset_id,
    category: row.category ?? "",
    daily_rate: Number.isFinite(n) ? n : null,
    // `on_rent_since` is the screen's name for the delivery stamp.
    on_rent_since: row.delivered_at ?? undefined,
    days_on_rent: daysOnRent(row.delivered_at, row.returned_at),
  };
}

/**
 * Not a stored column — derived from the two stamps that are. Returns
 * `undefined` when the asset has not been delivered, so the caller's own
 * fallback decides rather than a zero standing in for "unknown".
 */
function daysOnRent(deliveredAt?: string | null, returnedAt?: string | null): number | undefined {
  if (!deliveredAt) return undefined;
  const start = new Date(deliveredAt);
  if (Number.isNaN(start.getTime())) return undefined;
  const end = returnedAt ? new Date(returnedAt) : new Date();
  if (Number.isNaN(end.getTime())) return undefined;
  return Math.max(0, Math.floor((end.getTime() - start.getTime()) / 86_400_000));
}

/**
 * A prefill line as POST /tickets/ai-prefill sends it. `rate` is null when no
 * rate is on file (`rate_missing_reason` says why), and then `subtotal` is too.
 * Once a line's days or rate are edited its subtotal is null: the money is the
 * server's, worked out when the ticket is saved.
 */
interface AIDraftLineItem {
  asset_name: string;
  asset_id: string;
  category: string;
  days: number | null;
  rate: number | null;
  subtotal: number | null;
  rate_missing_reason?: string | null;
}

interface AIDraft {
  ticket_number: string;
  line_items: AIDraftLineItem[];
  /** All-or-nothing: null while any line is unpriced or tax is unresolved. */
  subtotal: number | null;
  tax_amount: number | null;
  tax_type: string | null;
  total: number | null;
  notes: string;
  /** Always null today: no model is called (api#942). */
  confidence_score: number | null;
  period_start?: string;
  period_end?: string;
}


type DraftStep = "select" | "loading" | "review";

const nativeSelect =
  "h-9 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none";

export default function NewRentalFieldTicketDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const timeZone = useTenantTimeZone();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  // AI Draft Engine state
  const [draftStep, setDraftStep] = useState<DraftStep>("select");
  const [activeOrders, setActiveOrders] = useState<OrderOption[]>([]);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [selectedOrderDetail, setSelectedOrderDetail] = useState<OrderOption | null>(null);
  const [, setOrderAssets] = useState<OrderAsset[]>([]);
  const [aiDraft, setAiDraft] = useState<AIDraft | null>(null);
  const [, setDraftLoading] = useState(false);

  // HITL Review editable state
  const [editTicketNumber, setEditTicketNumber] = useState("");
  /** The number the prefill PEEKED. It is not reserved, so it is sent only if the user changed it. */
  const [draftTicketNumber, setDraftTicketNumber] = useState("");
  const [editPeriodStart, setEditPeriodStart] = useState("");
  const [editPeriodEnd, setEditPeriodEnd] = useState("");
  const [editLineItems, setEditLineItems] = useState<AIDraftLineItem[]>([]);
  const [editNotes, setEditNotes] = useState("");


  // The review's totals are the prefill's, as the API sent them. They used to
  // be summed here with GST at a hard-coded 5% — wrong for every province but
  // AB's. Once a line is edited they no longer describe the lines, so they read
  // "—" until the server prices the saved ticket.
  const draftEdited = aiDraft != null && editLineItems.some((li, i) =>
    li.days !== aiDraft.line_items[i]?.days || li.rate !== aiDraft.line_items[i]?.rate);

  function resetDraftEngine() {
    setDraftStep("select");
    setSelectedOrderId("");
    setSelectedOrderDetail(null);
    setOrderAssets([]);
    setAiDraft(null);
    setEditLineItems([]);
    setEditNotes("");
    setEditTicketNumber("");
    setDraftTicketNumber("");
    setEditPeriodStart("");
    setEditPeriodEnd("");
  }

  // Fetch active orders for step 1
  const fetchActiveOrders = useCallback(async () => {
    try {
      const res = await api.rentals.orders.list({ status: "active" });
      setActiveOrders(unwrapArray<OrderOption>(res.data));
      setOrdersError(null);
    } catch (err) {
      // An empty picker and a picker that failed to load look identical, and
      // the empty one invites the user to go create an order that already
      // exists. Say which one this is.
      setActiveOrders([]);
      setOrdersError(getApiErrorMessage(err, "Could not load active rental orders."));
    }
  }, []);

  // Step 2: Fetch order detail + assets then call AI prefill
  async function handleOrderSelect(orderId: string) {
    setSelectedOrderId(orderId);
    setDraftStep("loading");
    setDraftLoading(true);

    try {
      // Fetch order detail
      let orderDetail: OrderOption | null = null;
      let assets: OrderAsset[] = [];

      try {
        const orderRes = await api.rentals.orders.get(orderId);
        const od = orderRes.data?.data ?? orderRes.data;
        orderDetail = od as OrderOption;
      } catch {
        orderDetail = activeOrders.find((o) => o.id === orderId) ?? null;
      }

      setSelectedOrderDetail(orderDetail);

      // Fetch order assets
      try {
        // `/orders/:id/assets` is the route that answers this question.
        //
        // What ran here was `orders.list({ order_id })` — and `GET /orders`
        // reads only `status` and `customer_id`, so `order_id` was dropped and
        // the call returned 200 with EVERY order in the company. `Array.isArray`
        // passed, the orders were treated as this order's assets, and the AI
        // prefill below priced a field ticket from them. No error, no empty
        // state, just the wrong rows with a 200 on them.
        const assetsRes = await api.rentals.orders.getAssets(orderId);
        const body = assetsRes.data;
        const assetData = body && typeof body === 'object' && 'data' in body ? body.data : body;
        if (Array.isArray(assetData)) assets = (assetData as OrderAssetApiRow[]).map(normalizeOrderAsset);
      } catch (assetErr) {
        // No stand-in assets. Two invented ones with plausible names and day
        // counts priced a ticket the customer would have been billed from.
        // Let the outer catch report it and send the user back to step 1.
        throw assetErr;
      }

      setOrderAssets(assets);

      // Build AI payload
      // The billing period is counted in the TENANT's days (companies.time_zone),
      // not the device's — an evening draft in Alberta must not end tomorrow.
      // With no zone there is no honest period, so nothing is sent.
      const today = todayCalendarDate(timeZone);
      const thirtyDaysAgo = tenantTodayDate(timeZone);
      if (!today || !thirtyDaysAgo) {
        throw new Error("Your company's time zone is not loaded yet — try again in a moment.");
      }
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const periodStart = toCalendarDateString(thirtyDaysAgo);

      /**
       * Only what POST /tickets/ai-prefill reads (#815): the period and, per
       * asset, `asset_id` plus whatever is KNOWN of `daily_rate`,
       * `days_on_rent` and `on_rent_since`. A sent value beats the server's
       * own, so an unknown one is left out: `days_on_rent ?? 30` billed every
       * undelivered asset thirty days, and a missing rate went as 0. The
       * customer, well and LSD keys were never read ("Unknown" and
       * "00-00-000-00" went nowhere).
       */
      const aiPayload = {
        period_start: periodStart,
        period_end: today,
        assets: assets.map((a) => ({
          asset_id: a.asset_id,
          ...(a.daily_rate != null ? { daily_rate: a.daily_rate } : {}),
          ...(a.days_on_rent !== undefined ? { days_on_rent: a.days_on_rent } : {}),
          ...(a.on_rent_since ? { on_rent_since: a.on_rent_since } : {}),
        })),
      };

      // Call AI prefill via backend (same pattern as casing running)
      let draft: AIDraft;
      try {
        const aiRes = await api.rentals.tickets.aiPrefill(orderId, aiPayload);
        draft = (aiRes.data?.data ?? aiRes.data) as AIDraft;
      } catch (aiErr) {
        // The local draft minted its own ticket_number from a hardcoded
        // sequence base. POST /tickets stores ticket_number verbatim, so that
        // invented number became the real one on the next save.
        throw aiErr;
      }

      setDraftTicketNumber(draft.ticket_number ?? "");
      setEditTicketNumber(draft.ticket_number);
      setEditPeriodStart(draft.period_start ?? periodStart);
      setEditPeriodEnd(draft.period_end ?? today);
      setEditLineItems(draft.line_items.map((li) => ({
        ...li,
        days: parseMoney(li.days),
        rate: parseMoney(li.rate),
        subtotal: parseMoney(li.subtotal),
      })));
      setAiDraft({
        ...draft,
        line_items: draft.line_items.map((li) => ({ ...li, days: parseMoney(li.days), rate: parseMoney(li.rate), subtotal: parseMoney(li.subtotal) })),
      });
      setEditNotes(draft.notes);
      setDraftStep("review");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to prepare ticket draft"));
      setDraftStep("select");
    } finally {
      setDraftLoading(false);
    }
  }

  // Update a line. Days or rate: blank is missing (null), never 0, and the
  // line's amount is the server's from here on — not days × rate in the browser.
  function updateLineItem(idx: number, field: keyof AIDraftLineItem, value: number | string) {
    setEditLineItems((prev) => {
      const next = [...prev];
      const item = { ...next[idx] };
      if (field === "days" || field === "rate") {
        const n = value === "" ? null : Number(value);
        item[field] = n != null && Number.isFinite(n) ? n : null;
        item.subtotal = null;
      } else if (field === "asset_name") {
        item.asset_name = String(value);
      }
      next[idx] = item;
      return next;
    });
  }

  // Step 4: Submit approved ticket
  async function handleApproveTicket() {
    setCreating(true);
    /**
     * What POST /tickets reads, and only what is known (#815). Gone:
     * "Unknown" / "N/A" for a missing well, customer or PO (stored verbatim);
     * `dailyRate` as the SUM of every line's rate and `totalDays` as the first
     * line's days (single-figure columns that only mean something with one
     * line, so they are sent only then); and the `lineItems` count (never
     * read). The ticket number is left to the server, which allocates one
     * under its lock, unless the user typed a different one.
     */
    const well = wellOf(selectedOrderDetail);
    const customer = customerOf(selectedOrderDetail);
    const po = selectedOrderDetail?.afe_number;
    const single = editLineItems.length === 1 ? editLineItems[0] : null;
    const typedNumber = editTicketNumber.trim();
    const payload = {
      ...(typedNumber && typedNumber !== draftTicketNumber ? { ticket_number: typedNumber } : {}),
      ...(well ? { wellName: well } : {}),
      ...(customer ? { customer } : {}),
      ...(po ? { poNumber: po } : {}),
      order: selectedOrderId,
      periodStart: editPeriodStart,
      periodEnd: editPeriodEnd,
      ...(single && single.days != null ? { totalDays: single.days } : {}),
      ...(single && single.rate != null ? { dailyRate: single.rate } : {}),
      // No money: the server computes amounts, subtotal, tax and total and
      // ignores any sent (#815 1c). An unknown days or rate is left out.
      line_items: editLineItems.map((li) => ({
        description: li.asset_name,
        asset_id: li.asset_id,
        ...(li.days != null ? { days: li.days } : {}),
        ...(li.rate != null ? { rate: li.rate } : {}),
      })),
      notes: editNotes,
    };

    try {
      const response = await api.rentals.tickets.create(payload);
      const created: FieldTicket = response.data?.ticket ?? response.data?.data ?? response.data;
      const number = (created as { ticket_number?: string } | undefined)?.ticket_number;
      toast.success(number ? `Field ticket ${number} created` : "Field ticket created");
      onOpenChange(false);
      resetDraftEngine();
      await queryClient.invalidateQueries({ queryKey: ["rentals", "fieldTickets"] });
    } catch (err) {
      // Dialog stays open with the reviewed line items intact. The local
      // ticket this used to push into the cache carried a ticket number the
      // server had never issued.
      toast.error(getApiErrorMessage(err, "Failed to create field ticket"));
    } finally {
      setCreating(false);
    }
  }

  // Opening starts from a clean draft with fresh orders, as the band button did.
  useEffect(() => {
    if (open) {
      resetDraftEngine();
      void fetchActiveOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on open only
  }, [open]);

  return (
    <>
      {/* AI-Powered New Field Ticket Dialog */}
      <Dialog open={open} onOpenChange={(next) => {
        if (!next) { resetDraftEngine(); }
        onOpenChange(next);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-fieldiq-accent-signal" aria-hidden="true" />
              {draftStep === "select" && "New Field Ticket — Select Order"}
              {draftStep === "loading" && "Preparing AI Draft..."}
              {draftStep === "review" && "Review AI-Generated Ticket"}
            </DialogTitle>
          </DialogHeader>

          {/* Step 1: Order Selection */}
          {draftStep === "select" && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">
                Select an active rental order. The AI will automatically calculate line items, rates, and totals.
              </p>
              <div className="grid gap-2">
                <Label>Order</Label>
                {/* Native and labelled: ui/select prints the raw value (an order id) in its trigger. */}
                <select
                  aria-label="Order"
                  value={selectedOrderId}
                  onChange={(e) => setSelectedOrderId(e.target.value)}
                  className={nativeSelect}
                >
                  <option value="">{activeOrders.length > 0 ? "Select an active order..." : ordersError ?? "No active rental orders."}</option>
                  {activeOrders.map((o) => (
                    <option key={o.id} value={o.id}>
                      {[orderNumberOf(o) ?? o.id, customerOf(o) ?? "\u2014", wellOf(o)].filter(Boolean).join(" \u2014 ")}
                    </option>
                  ))}
                </select>
                {ordersError && (
                  <p className="text-xs text-destructive">{ordersError}</p>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { onOpenChange(false); resetDraftEngine(); }}>
                  Cancel
                </Button>
                <Button
                  disabled={!selectedOrderId}
                  onClick={() => handleOrderSelect(selectedOrderId)}
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate AI Draft
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 2: Loading */}
          {draftStep === "loading" && (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <Loader2 className="h-10 w-10 animate-spin text-fieldiq-accent-signal" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">Preparing ticket draft...</p>
              <p className="text-xs text-muted-foreground">Fetching order data and calculating line items</p>
            </div>
          )}

          {/* Step 3: HITL Review */}
          {draftStep === "review" && aiDraft && (
            <div className="space-y-5 py-2">
              {/* Confidence indicator */}
              {typeof aiDraft.confidence_score === "number" && aiDraft.confidence_score < 0.85 && (
                <div className="flex items-center gap-2 rounded-fi-control border border-fieldiq-border bg-fi-amber/10 p-3">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-fieldiq-text" aria-hidden="true" />
                  <p className="text-xs text-fieldiq-text">
                    Please verify rates — some data was uncertain (confidence: {Math.round(aiDraft.confidence_score * 100)}%)
                  </p>
                </div>
              )}
              {typeof aiDraft.confidence_score === "number" && aiDraft.confidence_score >= 0.85 && (
                <div className="flex items-center gap-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 p-3">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-fieldiq-text" aria-hidden="true" />
                  <p className="text-xs text-fieldiq-text">
                    High confidence draft ({Math.round(aiDraft.confidence_score * 100)}%) — review and approve
                  </p>
                </div>
              )}

              {/* Ticket Number + Dates */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Ticket Number</Label>
                  <Input
                    value={editTicketNumber}
                    onChange={(e) => setEditTicketNumber(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Period Start</Label>
                  <Input
                    type="date"
                    value={editPeriodStart}
                    onChange={(e) => setEditPeriodStart(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Period End</Label>
                  <Input
                    type="date"
                    value={editPeriodEnd}
                    onChange={(e) => setEditPeriodEnd(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
              </div>

              {/* Order context */}
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <span className="text-muted-foreground">Customer: </span>
                  <span className="font-semibold text-foreground">{customerOf(selectedOrderDetail) ?? "\u2014"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Well: </span>
                  <span className="font-semibold text-foreground">{wellOf(selectedOrderDetail) ?? "\u2014"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">LSD: </span>
                  <span className="font-mono text-foreground">{selectedOrderDetail?.lsd ?? "\u2014"}</span>
                </div>
              </div>

              {/* Line Items Table (editable) */}
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Asset</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">ID</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Days</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Rate</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {editLineItems.map((li, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2">
                          <Input
                            value={li.asset_name}
                            onChange={(e) => updateLineItem(idx, "asset_name", e.target.value)}
                            className="h-7 text-xs border-transparent hover:border-border focus:border-primary"
                          />
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground font-mono">{li.asset_id}</td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            aria-label={`Days for ${li.asset_name}`}
                            value={li.days ?? ""}
                            onChange={(e) => updateLineItem(idx, "days", e.target.value)}
                            className="h-7 text-xs text-right w-16 ml-auto border-transparent hover:border-border focus:border-primary"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            aria-label={`Rate for ${li.asset_name}`}
                            placeholder="No rate"
                            value={li.rate ?? ""}
                            onChange={(e) => updateLineItem(idx, "rate", e.target.value)}
                            className="h-7 text-xs text-right w-20 ml-auto border-transparent hover:border-border focus:border-primary"
                          />
                        </td>
                        <td data-testid={`draft-line-subtotal-${idx}`} className="px-3 py-2 text-right text-xs font-medium">{fmtCurrency(
                          li.days === aiDraft.line_items[idx]?.days && li.rate === aiDraft.line_items[idx]?.rate
                            ? aiDraft.line_items[idx]?.subtotal ?? null
                            : null,
                        )}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-muted/30 border-t border-border">
                    <tr>
                      <td colSpan={4} className="px-3 py-2 text-right text-xs">Subtotal</td>
                      <td data-testid="draft-subtotal" className="px-3 py-2 text-right text-xs font-medium">{fmtCurrency(draftEdited ? null : aiDraft.subtotal)}</td>
                    </tr>
                    <tr>
                      <td data-testid="draft-tax-label" colSpan={4} className="px-3 py-2 text-right text-xs">{(!draftEdited && aiDraft.tax_type) || "Tax"}</td>
                      <td data-testid="draft-tax" className="px-3 py-2 text-right text-xs font-medium">{fmtCurrency(draftEdited ? null : aiDraft.tax_amount)}</td>
                    </tr>
                    <tr className="bg-muted/50">
                      <td colSpan={4} className="px-3 py-2 text-right text-sm font-semibold">Total</td>
                      <td data-testid="draft-total" className="px-3 py-2 text-right font-bold">{fmtCurrency(draftEdited ? null : aiDraft.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {draftEdited && (
                <p data-testid="draft-totals-note" className="text-xs text-muted-foreground">Totals are worked out when the ticket is saved.</p>
              )}

              {/* AI Notes (editable) */}
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={2}
                  className="text-sm"
                />
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => {
                  setDraftStep("select");
                  setAiDraft(null);
                }}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit &amp; Revise
                </Button>
                <Button onClick={handleApproveTicket} disabled={creating}>
                  {creating ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Approve &amp; Create Ticket
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </>
  );
}
